import * as React from "react"
import { Appearance, TouchableOpacity, View, ViewStyle } from "react-native"
import { observer } from "mobx-react-lite"
import { Actionsheet, ActionsheetProps } from "../actionsheet/actionsheet"
import { Divider, useDisclose } from "native-base"
import { FC, PropsWithChildren, useCallback, useEffect, useState } from "react"
import { FormControl, FormControlProps } from "../form-control/form-control"
import { InputProps } from "../input/input"
import { Button } from "../button/button"
import { NumeralInput } from "../input/numeral-input/numeral-input"
import { LazyRender } from "../lazy-render/lazy-render"
import { delay } from "../../utils/delay"
import { Text } from "../text/text"
import { DateTimePicker } from "../date-time-picker/date-time-picker"
import { FlatList, FlatListProps, ListRenderItem } from "../flat-list/flat-list"

export interface SettingItemProps extends PropsWithChildren<any> {
  testID?: string,
  type: "action" | "picker" | "time-picker" | "text-input" | "numeral-input"
  value?: any,

  isDisabled?: boolean,

  onOpen?: () => void,
  onClose?: () => void,

  onPress?: () => void,
  onPressIn?: () => void,
  onPressOut?: () => void,

  onValueChange?: (value: any) => any

  actionsheetProps?: ActionsheetProps,
  renderActionSheetComponent?: (props: SettingItemProps) => React.Component,

  formControlProps?: FormControlProps,
  inputProps?: InputProps & Omit<FlatListProps<any>, "data"|"renderItem"> & { data?: Array<any>, renderItem?: ListRenderItem<any> | null | undefined },

  colorScheme?: "light" | "dark",
  actionSheetColorScheme?: "light" | "dark"
  clearable?: boolean,

  style?: ViewStyle
}

const FORM_CONTROL: ViewStyle = {
  paddingHorizontal: 16,
  marginBottom: 8
}

const BUTTON_CONTAINER: ViewStyle = {
  paddingBottom: 16,
  paddingHorizontal: 16,
}

const ACTIONSHEET: ViewStyle = {
  minHeight: 0,
  height: undefined
}

const ACTIONSHEET_ITEM: ViewStyle = {
  minHeight: 44,
  flexDirection: "row",
  alignItems: "center",
  paddingHorizontal: 16,
}

const ActionsheetComponent = ({
  type,
  formControlProps = {} as FormControlProps,
  inputProps = {} as InputProps & Omit<FlatListProps<any>, "data"|"renderItem"> & { data?: Array<any>, renderItem?: ListRenderItem<any> | null | undefined },
  colorScheme,
  defaultValue,
  onValueChange: onValueChangeProp
}) => {
  const { inputProps: formControlInputProps = {}, ...restFormControlProps } = formControlProps

  const [ loading, setLoading ] = useState<boolean>(false)
  const [ value, setValue ] = useState<any>(defaultValue)

  const onValueChange = useCallback(async (value) => {
    setLoading(true)
    await delay(1)
    onValueChangeProp && onValueChangeProp(value)
  }, [onValueChangeProp])

  switch (type) {
    case "date-picker":
      return (
        <FormControl
          colorScheme={colorScheme}
          style={FORM_CONTROL}
          {...formControlProps}
        >
          <DateTimePicker
            colorScheme={colorScheme}
            value={value}
            onValueChange={onValueChange}
            {...inputProps}
          />
        </FormControl>
      )
    case "time-picker":
      return (
        <FormControl
          colorScheme={colorScheme}
          style={FORM_CONTROL}
          {...formControlProps}
        >
          <DateTimePicker
            colorScheme={colorScheme}
            value={value}
            onValueChange={onValueChange}
            format="HH:mm:00"
            dateTimePickerProps={{ mode: "time", is24Hour: true }}
            {...inputProps}
          />
        </FormControl>
      )
    case "picker":
      // eslint-disable-next-line no-case-declarations
      const { renderItem, ...restInputProps } = inputProps

      return (
        <FlatList
          renderItem={(info) => (
            <TouchableOpacity onPress={() => onValueChange(info.item.id)}>
              <View style={ACTIONSHEET_ITEM}>
                {renderItem && renderItem(info) || <Text
                  text={info.item.name}
                  preset={info.item.id === value ? "title4" : "body2"}
                  colorScheme={colorScheme} isTruncated
                />}
              </View>
            </TouchableOpacity>
          )}
          keyExtractor={(item, index) => `${index}`}
          estimatedItemSize={44}
          keyboardShouldPersistTaps="handled"
          {...restInputProps}
        />
      )
    case "numeral-input":
      return (
        <>
          <FormControl
            colorScheme={colorScheme}
            inputProps={{
              autoCapitalize: "none",
              autoComplete: "off",
              autoCorrect: false,
              textContentType: "none",
              keyboardType: "numeric",
              returnKeyType: "go",
              autoFocus: true,
              defaultValue,
              onSubmitEditing: () => onValueChange(value),
              ...formControlInputProps,

            }}
            style={FORM_CONTROL}
            {...formControlProps}
          >
            {(props) => (
              <NumeralInput
                colorScheme={colorScheme}
                onChangeNumber={setValue}
                defaultValue={defaultValue}
                {...props}
                {...inputProps} />
            )}
          </FormControl>
          <View style={BUTTON_CONTAINER}>
            <Button
              testID="jala:id/save-button"
              tx="save"
              colorScheme={colorScheme}
              loading={loading}
              onPress={() => onValueChange(value)}
            />
          </View>
        </>
      );
    case "text-input":
    default:
      return (
        <>
          <FormControl
            colorScheme={colorScheme}
            inputProps={{
              autoCapitalize: "none",
              autoComplete: "off",
              autoCorrect: false,
              textContentType: "none",
              keyboardType: "default",
              returnKeyType: "go",
              autoFocus: true,
              defaultValue,
              onChangeText: setValue,
              onSubmitEditing: () => onValueChange(value),
              ...formControlInputProps,
              ...inputProps
            }}
            style={FORM_CONTROL}
            {...restFormControlProps}
          />
          <View style={BUTTON_CONTAINER}>
            <Button
              testID="jala:id/save-button"
              tx="save"
              colorScheme={colorScheme}
              loading={loading}
              onPress={() => onValueChange(value)}
            />
          </View>
        </>
      );
  }
}

/**
 * Describe your component here
 */
export const SettingItem: FC<SettingItemProps> = observer(function SettingItem(props) {
  const {
    testID = "jala:id/setting-item-button",
    type, value, formControlProps = {}, inputProps = {},
    isDisabled = false,
    onOpen: onOpenProp, onClose: onCloseProp, onPress, onPressIn, onPressOut,
    actionsheetProps: { title, titleTx, ...actionsheetProps } = {},
    colorScheme = Appearance.getColorScheme(),
    actionSheetColorScheme = colorScheme,
    clearable,
    children,
    style
  } = props

  const {
    renderActionSheetComponent,
    onValueChange: onValueChangeProp,
    ...actionsheetComponentProps
  } = props

  const { isOpen, onOpen, onClose: onCloseDisclose } = useDisclose()

  useEffect(() => {
    if (isOpen) onOpenProp && onOpenProp()
  }, [isOpen, onOpenProp])

  const onClose: () => void = useCallback(() => {
    onCloseProp && onCloseProp()
    onCloseDisclose()
  }, [onCloseDisclose, onCloseProp])

  const onValueChange: (value: any) => void = useCallback((value: any) => {
    onValueChangeProp && onValueChangeProp(value)
    onClose()
  }, [onClose, onValueChangeProp])

  return (
    <>
      <TouchableOpacity
        testID={testID}
        onPress={onPress || onOpen}
        onPressIn={onPressIn}
        onPressOut={onPressOut}
        disabled={isDisabled}
        style={style}
      >
        {children}
      </TouchableOpacity>
      {type !== 'action' && <Actionsheet
        colorScheme={actionSheetColorScheme}
        isOpen={isOpen}
        onClose={onClose}
        clearable={clearable}
        actionsheetStyle={type !== 'picker' ? ACTIONSHEET : undefined}
        titleTx={titleTx}
        title={title}
        {...actionsheetProps}
      >
        {!!(titleTx || title) && <Divider />}
        <LazyRender>
          {(renderActionSheetComponent && renderActionSheetComponent({ ...actionsheetComponentProps, onValueChange })) || <ActionsheetComponent
            type={type}
            formControlProps={formControlProps}
            inputProps={inputProps as any}
            colorScheme={colorScheme}
            defaultValue={value}
            onValueChange={onValueChange}
          />}
        </LazyRender>
      </Actionsheet>}
    </>
  )
})
